/**
 * The number on the app icon (F-120).
 *
 * Leo, 2026-09-16: *"whenever nabu sends a notification that is unread the icon should show
 * the unread indicator with a number, as soon as it's read the unread counter must reset to
 * the right number or disappear."*
 *
 * **Nothing here increments, and that is the entire design.** The Badging API offers no way to
 * read the current badge back, so a caller that adds one has to keep its own tally, and a tally
 * that is only ever adjusted drifts the first time an event is missed — a push that arrives
 * while the worker is being replaced, a device that was offline, a notification dismissed from
 * the lock screen. After that the icon carries a number nobody can account for, which is worse
 * than no badge at all because it teaches people the icon is lying. So every path sets an
 * absolute count taken from the server, and the only two operations are "show this many" and
 * "show none".
 *
 * The count itself comes from `unreadIn` in `@nabu/shared`, so the badge, the tabs and the API
 * cannot disagree about what unread means.
 *
 * **One icon, one number, and F-151 step 8 changed which number it is.** The Badging API puts a
 * single badge on a single home-screen icon; there is no per-tab badge to be had from the
 * platform and there never will be, so the tabs show their own counts inside the page and this
 * shows the learner's total. Nothing in this file changed for that, and that is the point — it
 * was already absolute and already had one caller's number to show. What changed is upstream:
 * the read marker moved from the learner's row to each plan's, so a plan's count is now that
 * plan's own, and `iconCount` in `tabs.ts` adds them up. Before it, the total was not a total —
 * over a thread that sorts by plan and then by time, one forward-only marker sits in whichever
 * plan sorts highest and a later message in any earlier plan never passes it, so the icon would
 * silently stop counting one of two plans the day the slot cap moved.
 *
 * The callers keep their contract with this file: pass an absolute count, never adjust one, and
 * **leave the badge alone rather than clearing it when nothing can be counted** — `iconCount`
 * answers `undefined` for that, and a cleared icon claims there is nothing waiting.
 */

/**
 * Whether this browser can show a badge at all.
 *
 * Read at call time rather than cached, because the answer is not stable in the way it looks:
 * on iOS the Badging API is present in a home-screen app and absent in the same browser's
 * ordinary tab, and a value captured at module load would be captured before the app knows
 * which it is.
 */
function supported(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

/**
 * Shows a count on the icon, or removes it when there is nothing unread.
 *
 * **Zero clears rather than showing a nought.** `setAppBadge(0)` is specified to show a badge
 * with no number — a dot — which on an icon reads as "something is waiting" and is the opposite
 * of what a count of zero means.
 *
 * Every failure is swallowed, deliberately and quietly. The call rejects on browsers that
 * expose the method behind a permission the learner has not granted, and throws outright in a
 * few embedded webviews; none of that is a reason for a chat screen to show an error, and a
 * badge that cannot be set is a cosmetic loss on a screen the learner is already looking at.
 *
 * Not awaited by callers for the same reason — the promise resolves when the platform has
 * updated the icon, which is of no interest to the render that asked for it.
 */
export async function showBadge(count: number): Promise<void> {
  if (!supported()) return;
  try {
    const n = Math.max(0, Math.floor(count));
    if (n === 0) await navigator.clearAppBadge?.();
    else await navigator.setAppBadge?.(n);
  } catch {
    // See above: unsupported, unpermitted and embedded-webview failures are all the same
    // non-event here.
  }
}

/**
 * Takes the badge off entirely.
 *
 * Its own function rather than `showBadge(0)` at the call sites, because the two have different
 * reasons: `showBadge` is "here is the current count, which happens to be none", and this is
 * "there is no longer a learner whose count this could be" — signing out, or an account that
 * has been deleted. Leaving a number on the icon after a sign-out shows one person's unread
 * count to whoever picks up the phone next.
 */
export async function clearBadge(): Promise<void> {
  if (!supported()) return;
  try {
    await navigator.clearAppBadge?.();
  } catch {
    // As above.
  }
}
